import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import SignaturePad from '../components/SignaturePad';
import { 
  ArrowLeft, 
  MapPin, 
  Clock, 
  Calendar, 
  Languages, 
  Building2, 
  User,
  Car,
  FileCheck,
  Loader2,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EncounterForm = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [existingEncounter, setExistingEncounter] = useState(null);
  const [travelEstimate, setTravelEstimate] = useState(null);
  
  const [formData, setFormData] = useState({
    // Client representative info
    representative_name: '',
    representative_title: '',
    client_signature_data: null,
    // Estimated times (from work order - read only)
    estimated_start_time: '',
    estimated_end_time: '',
    estimated_travel_time_minutes: '',
    estimated_travel_distance_miles: '',
    // Actual times (vendor enters)
    actual_start_time: '',
    actual_end_time: '',
    actual_travel_time_minutes: '',
    actual_travel_distance_miles: '',
    // Services & Notes
    services_rendered: '',
    notes: '',
    // Vendor signature
    vendor_signature_data: null
  });

  useEffect(() => {
    fetchAssignmentDetails();
  }, [assignmentId]);

  const fetchAssignmentDetails = async () => {
    try {
      // Fetch assignment
      const assignRes = await fetch(`${API_URL}/api/assignments/${assignmentId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (!assignRes.ok) throw new Error('Failed to fetch assignment');
      const assignData = await assignRes.json();
      setAssignment(assignData);

      // Fetch work order details
      const woRes = await fetch(`${API_URL}/api/work-orders/${assignData.work_order_id}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (woRes.ok) {
        const woData = await woRes.json();
        setWorkOrder(woData);
        
        // Calculate travel estimate if on-site
        if (woData.interpreting_details?.modality === 'onsite' && woData.interpreting_details?.location) {
          calculateTravelEstimate(woData.interpreting_details.location);
        }
        
        // Set estimated times from work order (read-only)
        // And default actual times to estimated
        if (woData.interpreting_details) {
          const startDt = woData.interpreting_details.start_datetime;
          const endDt = woData.interpreting_details.end_datetime;
          
          const estStartTime = startDt ? startDt.split('T')[1]?.substring(0, 5) : '';
          const estEndTime = endDt ? endDt.split('T')[1]?.substring(0, 5) : '';
          
          setFormData(prev => ({
            ...prev,
            estimated_start_time: estStartTime,
            estimated_end_time: estEndTime,
            actual_start_time: estStartTime, // Default to estimated
            actual_end_time: estEndTime       // Default to estimated
          }));
        }
      }

      // Check for existing encounter form
      const encounterRes = await fetch(`${API_URL}/api/encounters/assignment/${assignmentId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (encounterRes.ok) {
        const encounterData = await encounterRes.json();
        if (encounterData) {
          setExistingEncounter(encounterData);
        }
      }

    } catch (error) {
      console.error('Error fetching assignment:', error);
      toast.error('Failed to load assignment details');
    } finally {
      setLoading(false);
    }
  };

  const calculateTravelEstimate = async (destination) => {
    try {
      const res = await fetch(`${API_URL}/api/encounters/calculate-travel`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ destination })
      });
      
      if (res.ok) {
        const data = await res.json();
        setTravelEstimate(data);
      }
    } catch (error) {
      console.error('Error calculating travel:', error);
    }
  };

  const handleClientSignatureSave = (signatureData) => {
    setFormData(prev => ({ ...prev, client_signature_data: signatureData }));
    toast.success('Client signature captured');
  };

  const handleVendorSignatureSave = (signatureData) => {
    setFormData(prev => ({ ...prev, vendor_signature_data: signatureData }));
    toast.success('Interpreter signature captured');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_signature_data) {
      toast.error('Please capture the client representative signature');
      return;
    }
    
    if (!formData.vendor_signature_data) {
      toast.error('Please capture the interpreter signature');
      return;
    }
    
    if (!formData.representative_name) {
      toast.error('Please enter the representative name');
      return;
    }

    setSubmitting(true);
    
    try {
      const payload = {
        assignment_id: assignmentId,
        work_order_id: assignment.work_order_id,
        vendor_id: assignment.vendor_id,
        // Client Representative Info
        representative_name: formData.representative_name,
        representative_title: formData.representative_title,
        client_signature_data: formData.client_signature_data,
        // Estimated times
        estimated_start_time: formData.estimated_start_time,
        estimated_end_time: formData.estimated_end_time,
        estimated_travel_time_minutes: travelEstimate?.duration_minutes || null,
        estimated_travel_distance_miles: travelEstimate?.distance_miles || null,
        // Actual times
        actual_start_time: formData.actual_start_time,
        actual_end_time: formData.actual_end_time,
        actual_travel_time_minutes: formData.actual_travel_time_minutes ? parseInt(formData.actual_travel_time_minutes) : null,
        actual_travel_distance_miles: formData.actual_travel_distance_miles ? parseFloat(formData.actual_travel_distance_miles) : null,
        // Services & Notes
        services_rendered: formData.services_rendered,
        notes: formData.notes,
        // Vendor signature
        vendor_signature_data: formData.vendor_signature_data
      };

      const res = await fetch(`${API_URL}/api/encounters`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Encounter form submitted successfully!');
        navigate('/vendor-portal');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit encounter form');
      }
    } catch (error) {
      console.error('Error submitting encounter:', error);
      toast.error('Failed to submit encounter form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Assignment Not Found</h2>
            <p className="text-muted-foreground">The requested assignment could not be found.</p>
            <Button onClick={() => navigate('/vendor-portal')} className="mt-4">
              Return to Portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (existingEncounter) {
    return (
      <div className="container mx-auto p-6 max-w-3xl">
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Encounter Form Already Submitted</h2>
            <p className="text-muted-foreground mb-4">
              This assignment already has a completed encounter form submitted on{' '}
              {new Date(existingEncounter.submitted_at).toLocaleDateString()}.
            </p>
            <Button onClick={() => navigate('/vendor-portal')}>
              Return to Portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if assignment is completed
  if (assignment.status !== 'completed') {
    return (
      <div className="container mx-auto p-6 max-w-3xl">
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Assignment Not Yet Completed</h2>
            <p className="text-muted-foreground mb-4">
              Encounter forms can only be submitted after the assignment has been completed.
              Current status: <Badge variant="outline">{assignment.status}</Badge>
            </p>
            <Button onClick={() => navigate('/vendor-portal')}>
              Return to Portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/vendor-portal')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Encounter Form</h1>
          <p className="text-muted-foreground">Complete after on-site assignment</p>
        </div>
      </div>

      {/* Company Header */}
      <Card className="mb-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Bylyngo</h2>
              <p className="text-blue-100">Interpreting & Translation</p>
            </div>
            <FileCheck className="h-12 w-12 text-blue-200" />
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        {/* Assignment Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Assignment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-sm">Work Order</Label>
                <p className="font-medium">{workOrder?.work_order_number || assignment.work_order_id}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Service Type</Label>
                <p className="font-medium capitalize">{workOrder?.work_order_type || 'Interpreting'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Date</Label>
                <p className="font-medium">
                  {workOrder?.interpreting_details?.start_time 
                    ? new Date(workOrder.interpreting_details.start_time).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Scheduled Time</Label>
                <p className="font-medium">
                  {workOrder?.interpreting_details?.start_time 
                    ? new Date(workOrder.interpreting_details.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : 'N/A'}
                  {' - '}
                  {workOrder?.interpreting_details?.end_time 
                    ? new Date(workOrder.interpreting_details.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : 'N/A'}
                </p>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-sm flex items-center gap-1">
                  <Languages className="h-4 w-4" /> Languages
                </Label>
                <p className="font-medium">
                  {workOrder?.source_language} → {workOrder?.target_languages?.join(', ')}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm flex items-center gap-1">
                  <Building2 className="h-4 w-4" /> Client
                </Label>
                <p className="font-medium">{workOrder?.client_name || 'N/A'}</p>
              </div>
            </div>

            {workOrder?.interpreting_details?.location && (
              <div>
                <Label className="text-muted-foreground text-sm flex items-center gap-1">
                  <MapPin className="h-4 w-4" /> Location
                </Label>
                <p className="font-medium">{workOrder.interpreting_details.location}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Travel Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              Travel Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* System Estimated */}
            {travelEstimate && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <Label className="text-blue-700 font-medium">System Estimated (Approximate)</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <span className="text-sm text-muted-foreground">Distance:</span>
                    <p className="font-semibold">{travelEstimate.distance_miles?.toFixed(1)} miles</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Travel Time:</span>
                    <p className="font-semibold">{travelEstimate.duration_minutes} minutes</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actual Travel - Vendor Entered */}
            <div>
              <Label className="font-medium">Actual Travel (Enter Your Values)</Label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div className="space-y-2">
                  <Label htmlFor="actual_distance">Actual Distance (miles)</Label>
                  <Input
                    id="actual_distance"
                    type="number"
                    step="0.1"
                    value={formData.actual_travel_distance_miles}
                    onChange={(e) => setFormData({ ...formData, actual_travel_distance_miles: e.target.value })}
                    placeholder="e.g., 15.5"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="actual_time">Actual Travel Time (minutes)</Label>
                  <Input
                    id="actual_time"
                    type="number"
                    value={formData.actual_travel_time_minutes}
                    onChange={(e) => setFormData({ ...formData, actual_travel_time_minutes: e.target.value })}
                    placeholder="e.g., 25"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actual Service Times */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Actual Service Times
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="actual_start">Actual Start Time *</Label>
                <Input
                  id="actual_start"
                  type="time"
                  value={formData.actual_start_time}
                  onChange={(e) => setFormData({ ...formData, actual_start_time: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="actual_end">Actual End Time *</Label>
                <Input
                  id="actual_end"
                  type="time"
                  value={formData.actual_end_time}
                  onChange={(e) => setFormData({ ...formData, actual_end_time: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div className="mt-4 space-y-2">
              <Label htmlFor="services">Services Rendered</Label>
              <Textarea
                id="services"
                value={formData.services_rendered}
                onChange={(e) => setFormData({ ...formData, services_rendered: e.target.value })}
                placeholder="Brief description of interpreting services provided..."
                rows={2}
              />
            </div>

            <div className="mt-4 space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes or comments..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Client Representative Signature */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Client Representative Signature
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rep_name">Representative Name *</Label>
                <Input
                  id="rep_name"
                  value={formData.representative_name}
                  onChange={(e) => setFormData({ ...formData, representative_name: e.target.value })}
                  placeholder="Full name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rep_title">Title / Position</Label>
                <Input
                  id="rep_title"
                  value={formData.representative_title}
                  onChange={(e) => setFormData({ ...formData, representative_title: e.target.value })}
                  placeholder="e.g., Office Manager"
                />
              </div>
            </div>

            <Separator />

            <div>
              <Label className="mb-3 block">Client Signature *</Label>
              {formData.client_signature_data ? (
                <div className="space-y-3">
                  <div className="border rounded-lg p-2 bg-gray-50">
                    <img 
                      src={formData.client_signature_data} 
                      alt="Client signature" 
                      className="max-h-32 mx-auto"
                    />
                  </div>
                  <div className="text-center">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setFormData({ ...formData, client_signature_data: null })}
                    >
                      Clear & Re-sign
                    </Button>
                  </div>
                </div>
              ) : (
                <SignaturePad onSave={handleClientSignatureSave} />
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              By signing above, the client representative confirms that interpreting services were rendered as described.
            </p>
          </CardContent>
        </Card>

        {/* Interpreter Signature */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Interpreter Signature
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Interpreter:</strong> {assignment?.vendor_name || 'N/A'}
              </p>
            </div>

            <div>
              <Label className="mb-3 block">Your Signature *</Label>
              {formData.vendor_signature_data ? (
                <div className="space-y-3">
                  <div className="border rounded-lg p-2 bg-gray-50">
                    <img 
                      src={formData.vendor_signature_data} 
                      alt="Interpreter signature" 
                      className="max-h-32 mx-auto"
                    />
                  </div>
                  <div className="text-center">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setFormData({ ...formData, vendor_signature_data: null })}
                    >
                      Clear & Re-sign
                    </Button>
                  </div>
                </div>
              ) : (
                <SignaturePad onSave={handleVendorSignatureSave} />
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              By signing above, I confirm that I provided the interpreting services as described and the information is accurate.
            </p>
          </CardContent>
        </Card>

        {/* Print Notice */}
        <Card className="mb-6 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-sm text-amber-800">
              <strong>📄 Need a paper copy?</strong> After submission, you can download and print a PDF version of this encounter form from the confirmation page or your portal history.
            </p>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex gap-4 justify-end">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/vendor-portal')}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={submitting || !formData.client_signature_data || !formData.vendor_signature_data}
            className="bg-green-600 hover:bg-green-700"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <FileCheck className="h-4 w-4 mr-2" />
                Submit Encounter Form
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EncounterForm;
